import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles } from './login-styles';
import { styles as sharedStyles } from '../../styles/shared-styles';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { write } from '../../language/lang';
import { resolveRouterPath, router } from '../../router';
import { FacebookAuth } from '../../authentication/facebook-auth';
import { GoogleAuth } from '../../authentication/google-auth';
import { ApiFactory } from '../../api/easypos-api';
import { UserProfile } from '../../datatypes/user-profile';

const PASSWORD_MIN_LENGTH = 10;

@customElement('app-login')
export class AppLogin extends WithUserProfileConsumer(LitElement) {
  static styles = [sharedStyles, styles];

  @state() private _mode: 'signin' | 'register' = 'signin';
  @state() private _email = '';
  @state() private _password = '';
  @state() private _displayName = '';
  @state() private _twoFactorCode = '';
  @state() private _busy = false;
  @state() private _errorMessage: string | null = null;
  // Set when app-index's device-binding gate rejected the previous
  // login. Stored in localStorage by the gate so we can read + clear it
  // here on a clean page load.
  @state() private _bindingRejection: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    // If the user is already logged in when the login page mounts (e.g.
    // they typed /login directly while authenticated), bounce them to the
    // dashboard. The refresh-while-logged-in case is handled by app-index's
    // _routeForShopState because the userProfile context isn't populated
    // yet at this point in the cold-start sequence.
    if (this.userProfile) {
      router.navigate(resolveRouterPath('dashboard'));
    }
    try {
      const r = localStorage.getItem('device-binding-rejection');
      if (r) {
        this._bindingRejection = r;
        localStorage.removeItem('device-binding-rejection');
      }
    } catch {
      // localStorage unavailable; nothing to surface.
    }
  }

  // Catch the case where the userProfile context arrives AFTER this
  // component has already mounted on /login (cold-start refresh). The
  // mixin invokes this whenever the consumed userProfile changes; if it
  // becomes set while we're still on the login page, send the user to
  // the dashboard.
  doUserProfileChanged() {
    if (this.userProfile) {
      router.navigate(resolveRouterPath('dashboard'));
    }
  }

  private handleFacebookLogin() {
    void FacebookAuth.loginWithFacebook();
  }

  private handleGoogleLogin() {
    void GoogleAuth.startGoogleLogin();
  }

  private _setMode(mode: 'signin' | 'register') {
    if (this._busy) return;
    this._mode = mode;
    this._errorMessage = null;
  }

  private _onInput(field: 'email' | 'password' | 'displayName' | 'twoFactor') {
    return (e: Event) => {
      const value = (e.target as HTMLInputElement).value;
      switch (field) {
        case 'email':
          this._email = value;
          break;
        case 'password':
          this._password = value;
          break;
        case 'displayName':
          this._displayName = value;
          break;
        case 'twoFactor':
          this._twoFactorCode = value;
          break;
      }
    };
  }

  private async _handleSubmit(e: Event) {
    e.preventDefault();
    if (this._busy) return;
    this._errorMessage = null;

    const email = this._email.trim();
    const password = this._password;

    if (!email || !password) {
      this._errorMessage = 'Email and password are required.';
      return;
    }

    if (this._mode === 'register' && password.length < PASSWORD_MIN_LENGTH) {
      this._errorMessage = `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
      return;
    }

    this._busy = true;
    try {
      const api = ApiFactory.anonymous();
      const response =
        this._mode === 'signin'
          ? await api.login(email, password, this._twoFactorCode || undefined)
          : await api.register(
              email,
              password,
              this._displayName.trim() || undefined
            );

      if (!response.success || !response.data) {
        this._errorMessage = response.error ?? 'Login failed.';
        return;
      }

      const easyposToken = response.data;
      const profile = new UserProfile({
        id: easyposToken.id,
        displayName: easyposToken.displayName,
        emailAddress: easyposToken.emailAddress ?? email,
        authProvider: 'internal',
        accessToken: easyposToken,
      });
      window.dispatchEvent(
        new CustomEvent<UserProfile>('user-profile-set', { detail: profile })
      );
    } catch (err) {
      this._errorMessage =
        (err as Error)?.message ?? 'Unexpected error during sign in.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    const isSignin = this._mode === 'signin';

    return html`
      <div class="auth-shell">
        <aside class="brand-pane">
          <div class="brand-mark">
            <img
              class="brand-icon"
              src="/assets/icons/pag-asenso-icon.svg"
              alt=""
            />
            <span class="brand-name"
              >Pag<span class="brand-name-accent">-asenso</span></span
            >
          </div>
          <h1 class="brand-headline">
            ${write`${'ref:login:hero-title-byline'}The POS that grows with you, from entrepreneur to enterprise.`}
          </h1>
          <p class="brand-sub">
            ${write`${'ref:login:hero-sub'}Sales, inventory, stock takes, and reports — built for small cafes and shops, priced to keep more peso in your pocket.`}
          </p>
          <ul class="brand-features">
            <li>
              <sl-icon name="lightning-charge-fill"></sl-icon>
              <span
                >${write`${'ref:login:feature-fast'}Fast cashier register, made for the rush.`}</span
              >
            </li>
            <li>
              <sl-icon name="boxes"></sl-icon>
              <span
                >${write`${'ref:login:feature-stock'}Stock, waste, and stock-take tools that actually match real shop life.`}</span
              >
            </li>
            <li>
              <sl-icon name="cloud-arrow-up-fill"></sl-icon>
              <span
                >${write`${'ref:login:feature-offline'}Works offline — sales sync the moment you're back online.`}</span
              >
            </li>
          </ul>
        </aside>

        <sl-card class="auth-card">
          <h2 class="auth-title">
            ${
              isSignin
                ? write`${'ref:login:title-signin'}Welcome back`
                : write`${'ref:login:title-register'}Create your account`
            }
          </h2>
          <p class="auth-subtitle">
            ${
              isSignin
                ? write`${'ref:login:subtitle-signin'}Sign in to your shop to keep selling.`
                : write`${'ref:login:subtitle-register'}A few details and you're ready to set up your first shop.`
            }
          </p>

          <div class="auth-mode-switch" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected=${isSignin}
              class=${isSignin ? 'active' : ''}
              ?disabled=${this._busy}
              @click=${() => this._setMode('signin')}
            >
              ${write`${'ref:login:signin-tab'}Sign in`}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected=${!isSignin}
              class=${!isSignin ? 'active' : ''}
              ?disabled=${this._busy}
              @click=${() => this._setMode('register')}
            >
              ${write`${'ref:login:register-tab'}Create account`}
            </button>
          </div>

          <form class="auth-form" @submit=${this._handleSubmit}>
            <sl-input
              label=${write`${'ref:login:email'}Email`}
              type="email"
              autocomplete="email"
              required
              .value=${this._email}
              @sl-input=${this._onInput('email')}
            ></sl-input>

            ${
              !isSignin
                ? html`
                    <sl-input
                      label=${write`${'ref:login:display-name'}Display name (optional)`}
                      type="text"
                      autocomplete="name"
                      .value=${this._displayName}
                      @sl-input=${this._onInput('displayName')}
                    ></sl-input>
                  `
                : nothing
            }

            <sl-input
              label=${
                !isSignin
                  ? write`${'ref:login:password-new'}Password (min ${PASSWORD_MIN_LENGTH} characters)`
                  : write`${'ref:login:password'}Password`
              }
              type="password"
              autocomplete=${!isSignin ? 'new-password' : 'current-password'}
              password-toggle
              required
              .value=${this._password}
              @sl-input=${this._onInput('password')}
            ></sl-input>

            ${
              isSignin
                ? html`
                    <sl-input
                      label=${write`${'ref:login:two-factor'}Two-factor code (if enabled)`}
                      type="text"
                      autocomplete="one-time-code"
                      inputmode="numeric"
                      .value=${this._twoFactorCode}
                      @sl-input=${this._onInput('twoFactor')}
                    ></sl-input>
                  `
                : nothing
            }
            ${
              this._bindingRejection
                ? html`
                    <sl-alert
                      class="auth-error"
                      variant="warning"
                      open
                      closable
                    >
                      <sl-icon slot="icon" name="device-ssd"></sl-icon>
                      ${this._bindingRejection}
                    </sl-alert>
                  `
                : nothing
            }
            ${
              this._errorMessage
                ? html`
                    <sl-alert class="auth-error" variant="danger" open>
                      <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
                      ${this._errorMessage}
                    </sl-alert>
                  `
                : nothing
            }

            <sl-button
              type="submit"
              variant="primary"
              size="medium"
              ?loading=${this._busy}
              ?disabled=${this._busy}
            >
              ${
                isSignin
                  ? write`${'ref:login:signin-submit'}Sign in`
                  : write`${'ref:login:register-submit'}Create account`
              }
            </sl-button>

            ${
              !isSignin
                ? html`
                    <p class="auth-hint">
                      ${write`${'ref:login:bind-hint'}If this email is already linked to a Google or Facebook account, your password will be added to that existing account.`}
                    </p>
                  `
                : nothing
            }
          </form>

          <div class="or-divider">
            <span>${write`${'ref:login:or-continue'}or continue with`}</span>
          </div>

          <div class="login-buttons">
            <sl-button
              class="login-button"
              @click=${this.handleGoogleLogin}
              ?disabled=${this._busy}
            >
              <sl-icon
                slot="prefix"
                name="google"
                class="provider-icon google"
              ></sl-icon>
              ${write`${'ref:login:google'}Continue with Google`}
            </sl-button>
            <sl-button
              class="login-button"
              @click=${this.handleFacebookLogin}
              ?disabled=${this._busy}
            >
              <sl-icon
                slot="prefix"
                name="facebook"
                class="provider-icon facebook"
              ></sl-icon>
              ${write`${'ref:login:facebook'}Continue with Facebook`}
            </sl-button>
          </div>
        </sl-card>
      </div>

      <footer class="legal-footer">
        <a href="/privacy">${write`${'ref:legal:privacy'}Privacy`}</a>
        <span aria-hidden="true">·</span>
        <a href="/terms">${write`${'ref:legal:terms'}Terms`}</a>
      </footer>
    `;
  }
}
